import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import { hasRole } from '../plugins/hasRole'

import Login from '../views/Login.vue'
import Layout from '../views/Layout.vue'
import Dashboard from '../views/Dashboard.vue'
import Calendar from '../views/Calendar.vue'
import EventList from '../views/EventList.vue'
import Planner from '../views/Planner.vue'
import Me from '../views/Me.vue'
import PasskeyRequired from '../views/PasskeyRequired.vue'
import objectRoutes from '../views/object/routes'
import NotesPrint from '../views/object/NotesPrint.vue'
import clientRoutes from '../views/client/routes'
import userRoutes from '../views/user/routes'
import systemRoutes from '../views/system/routes'

const routes = [
  {
    path: '/',
    component: Layout,
    children: [{
      path: '',
      alias: '/dashboard',
      component: Dashboard
    }, {
      path: '/calendar/full',
      component: Calendar,
      name: 'calendar'
    }, {
      path: '/calendar/events',
      component: EventList
    }, {
      path: '/planner/:date?',
      component: Planner,
      name: 'planner',
      // A technician reads it; only the office fills it in (Planner.vue).
      meta: { roles: ['admin', 'secretary', 'tehnic'] }
    }, {
      // The old Seaded page lived here; its password, signature and Google
      // cards are on Minu seaded now. Kept as a way in: the Google callback
      // (server/google/routes.js) and old bookmarks still come here.
      path: '/settings',
      redirect: '/me'
    }, {
      // Minu seaded: each person's own, so every employee.
      path: '/me',
      component: Me,
      name: 'me'
    }]
      .concat(objectRoutes)
      .concat(clientRoutes)
      .concat(userRoutes)
      .concat(systemRoutes)
  },
  {
    // Outside the Layout on purpose: the nav drawer, app bar and tabs have no
    // business on a printed page. The beforeEach guard below still covers it —
    // it only exempts routes marked authNotRequired.
    path: '/object/:objectId/notes/print',
    name: 'object/notes/print',
    component: NotesPrint
  },
  {
    // Pääsuvõtmed Kohustuslik and no key yet (the guard below sends them here).
    path: '/passkey',
    name: 'passkey-required',
    component: PasskeyRequired
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      authNotRequired: true
    }
  },
  {
    // Vue Router 4 spells the catch-all as a named wildcard param; '*' is gone.
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  if (!(await store.dispatch('isAuthenticated') || to.matched.some((record) => record.meta.authNotRequired))) {
    return next('/login?to=' + to.path)
  }
  // Pääsuvõtmed Kohustuslik: only "Lisa pääsuvõti" until a key is added.
  if (store.state.mustAddPasskey && to.name !== 'passkey-required' && !to.matched.some((record) => record.meta.authNotRequired)) {
    return next({ name: 'passkey-required', query: { to: to.fullPath } })
  }
  if (!store.state.mustAddPasskey && to.name === 'passkey-required') {
    return next('/')
  }
  // meta.roles: the roles a page is for, the ones its API lets in (root, like on
  // the server, always). Anyone else is sent to the dashboard before the page
  // opens, rather than onto a page whose data would only come back 403. The menu
  // in Layout.vue leaves the same pages out.
  const required = to.matched.map((record) => record.meta.roles).filter(Boolean)
  if (required.some((roles) => !hasRole([...roles, 'root']))) {
    store.dispatch('showNotification', { text: 'Kasutajal puudub vajalik roll', color: 'red' })
    return next('/')
  }
  next()
})

// Backs out of a page that cannot be shown. Only ever a step back within the
// app (vue-router keeps the previous in-app entry in history.state.back): on a
// freshly opened tab history.back() would leave the site altogether, so there
// it goes to the start page instead, or stays if it already is there.
export function leavePage () {
  if (window.history.state && window.history.state.back) {
    router.go(-1)
  } else if (router.currentRoute.value.path !== '/') {
    router.replace('/')
  }
}

export default router
